import React from 'react';
import { useSelector } from 'react-redux';
import { selectors as SectionSelectors } from '@reducers/sections';
import Button from '@components/Atoms/Button/Button';
import WorksheetsButton from '@components/Component/Lesson/WorksheetsButton/WorksheetsButton';
import InformationCard from '@components/Component/InformationCard/InformationCard';
import { useSectionStudent } from '@reducers/sectionStudent/hooks';
import { useIsTeacher } from '@reducers/user/hooks';
import ShadowScrollbar from '@components/Scrollbar/ShadowScrollbars';
import { useGetActivities } from '@components/Component/Lesson/LessonContainer/hooks';
import { useGetPacingGuideLessons } from '@components/Component/Lesson/PacingGuide/hooks';
import { useParams, useHistory } from 'react-router-dom';
import PacingGuideInfoCard from '@components/Component/Lesson/PacingGuide/PacingGuideInfoCard';
import ActionCard from '@components/Component/OLP/tab/ActionCard';
import { useActivityGroups, useGetGroupActivityInfo } from '@components/Component/OLP/tab/hooks/query';
import useWebSocketListener from '@components/Component/OLP/tab/hooks/useWebSocketListener';
import { useCurrentGroup, useCurrentStudent, useIsAllActivityGroupsCompleted } from '@components/Component/OLP/tab/hooks/queryStore';
import { activityStatus, groupOLPStatus } from '@components/Component/OLP/tab/types';
import { trackPlatformEvent } from '@utils/platformEvents';
import ActivityFactorySkeleton from './ActivityFactory.skeleton';
import { useSearchParams } from '@utils/hooks/useSearchParams';
import { useUpdateIndividualGroupActivity } from '@components/Component/OLP/tab/hooks/mutation';
import TooltipDisabledButton from '@components/Generals/TooltipDisabledButton';
import { ButtonGroup } from '@mui/material';
import GroupActivity from '@containers/GroupActivity';
import ShowIf from '@components/Atoms/ShowIf';
import StartActivityProvider, { useStartActivity } from '@components/Component/OLP/tab/Provider/StartActivityProvider';
import EndSessionProvider, { useEndActivitySession } from '@components/Component/OLP/tab/Provider/EndSessionProvider';
import { PLATFORM_EVENT_TYPES } from '../../../../../constants/platformEvents.generated';
import BellringerContextDialog from './BellringerContextDialog';
import { useLaunchBellringerSession, bellringerReviewLocation } from './useLaunchBellringerSession';

export const notStartedImgUrlImg = '/images/notInitialized.svg';
const offlineActivityUrlImg = '/images/offline.svg';
const completedPracticeUrlImg = '/images/completed-practice.svg';
const absentStudentUrlImg = '/images/absent-student.svg';

interface ViewConfig {
  showSkeleton: boolean;
  onlineMode?: boolean;
  actionButtons?: React.ReactNode;
  img?: string;
  title?: string;
  description?: string;
  testId?: string;
}

const ActivityFactory = () => {
  const { lessonId, courseId, sectionId, subjectId, unitId } = useParams();
  const isTeacher = useIsTeacher();
  const sectionStudentList = useSectionStudent();
  const currentGroupActivity = useCurrentGroup();
  const isStudentAbsent = Object.keys(currentGroupActivity).length === 0;
  // It should be here to check and create groupActivityId.
  //  we still depend on redux- saga to create newGroupActivity records
  useGetActivities();
  const {
    data: {
      id: groupActivityId,
      number_of_questions: numberOfQuestions,
      online_activity_status: onlineActivityStatus = 0,
      lesson: activityLessonId,
    },
  } = useGetGroupActivityInfo(undefined, { refetchOnMount: 'always' });
  useActivityGroups(groupActivityId, { refetchOnMount: 'always' });
  const { data: pacingGuideLesson } = useGetPacingGuideLessons(lessonId);
  const pacingGuideLessonData = pacingGuideLesson?.[0];
  useWebSocketListener();
  const history = useHistory();
  const { 'pacing-guide-id': pacingGuideId } = useSearchParams();
  const { student_id: activityStudentId, online_response_id: onlineResponseId } = useCurrentStudent(currentGroupActivity);
  const isAllActivityGroupsCompleted = useIsAllActivityGroupsCompleted();
  const updateIndividualGroupActivity = useUpdateIndividualGroupActivity();
  const { handleEndSession } = useEndActivitySession();
  const { handleStartActivity } = useStartActivity();

  const [isBellringerDialogOpen, setIsBellringerDialogOpen] = React.useState(false);
  const launchBellringerSession = useLaunchBellringerSession();
  const allSections = useSelector(SectionSelectors.getSectionList);
  const teacherSections = React.useMemo(
    () =>
      (allSections || [])
        .filter((section) => String(section.course) === String(courseId))
        .map((section) => ({ id: section.id, name: section.name })),
    [allSections, courseId],
  );

  const launchBellringer = async ({
    intent,
    sectionId: chosenSectionId,
  }: {
    intent: 'teaching' | 'preview' | 'stay';
    sectionId: number | null;
  }) => {
    setIsBellringerDialogOpen(false);
    if (intent === 'teaching') {
      if (chosenSectionId == null) {
        // Invariant: a teaching selection always carries a section. Fail fast rather
        // than silently opening the current section (the preview path) with no event.
        // eslint-disable-next-line no-console
        console.error('Bell-Ringer: teaching intent received without a section.');
        return;
      }
      // Resolve the chosen class's OWN group activity (and switch context to it) so
      // the emitted section_id and group_activity_id always agree.
      await launchBellringerSession({ sectionId: chosenSectionId, activityLessonId });
      return;
    }
    // Preview / stay: no event; open the current section's bellringer as-is.
    history.push(
      bellringerReviewLocation({
        courseId,
        sectionId,
        subjectId,
        unitId,
        lessonId,
        groupActivityId,
        activityLessonId,
        pacingGuideId,
      }),
    );
  };

  const startActivityHandler = () => {
    if (isTeacher && groupActivityId) {
      handleStartActivity();
    } else {
      updateIndividualGroupActivity.mutate(
        {
          group_id: currentGroupActivity.id,
          student_id: activityStudentId,
          status: 1,
        },
        {
          onSuccess: () => {
            history.push(
              `/lessons/course/${courseId}/section/${sectionId}/subject/${subjectId}/unit/${unitId}/lesson/${lessonId}/online-activity/question/${groupActivityId}/${currentGroupActivity.id}/1/${onlineResponseId}/${activityStudentId}?${pacingGuideId ? `pacing-guide-id=${pacingGuideId}` : ''}`,
            );
          },
        },
      );
    }
  };
  const endActivityHandler = () => {
    handleEndSession();
  };

  const reviewQuestionsHandler = () => {
    if (isTeacher) {
      trackPlatformEvent(
        PLATFORM_EVENT_TYPES.GROUP_ACTIVITY_REVIEWED,
        { sectionId, lessonId },
        {
          group_activity_id: groupActivityId,
          activity_lesson_id: activityLessonId,
          unit_id: unitId,
          ...(pacingGuideId ? { pacing_guide_id: pacingGuideId } : {}),
        },
      );
      history.push({
        pathname: `/lessons/course/${courseId}/section/${sectionId}/subject/${subjectId}/unit/${unitId}/lesson/${lessonId}/online-activity/question/teacher/${groupActivityId}/review`,
        search: `?activityLessonId=${activityLessonId}${pacingGuideId ? `&pacing-guide-id=${pacingGuideId}` : ''}`,
      });
    } else {
      history.push({
        pathname: `/lessons/course/${courseId}/section/${sectionId}/subject/${subjectId}/unit/${unitId}/lesson/${lessonId}/online-activity/question/${groupActivityId}/${currentGroupActivity.id}/review/${onlineResponseId}/${activityStudentId}`,
        search: `?${pacingGuideId ? `pacing-guide-id=${pacingGuideId}` : ''}`,
      });
    }
  };

  const getViewConfig = (): ViewConfig => {
    // Online Activity view
    if (
      !!groupActivityId &&
      !!numberOfQuestions &&
      !(
        onlineActivityStatus === activityStatus.started &&
        currentGroupActivity.group_status === groupOLPStatus.ended
      ) &&
      !(!isTeacher && isStudentAbsent) &&
      !!sectionStudentList.length
    ) {
      const buttonConfig = {
        showStart: (isTeacher && onlineActivityStatus === activityStatus.notStarted) || (isTeacher && onlineActivityStatus === activityStatus.ended) || (!isTeacher && onlineActivityStatus !== activityStatus.ended),
        showEnd: isTeacher && onlineActivityStatus === activityStatus.started,
        showReview: isTeacher || onlineActivityStatus === activityStatus.ended,
        showBellRinger: isTeacher,
      };
      return {
        showSkeleton: false,
        onlineMode: true,
        actionButtons: (
          <>
            {buttonConfig.showStart && (
              <TooltipDisabledButton
                disabled={!isAllActivityGroupsCompleted}
                title="All groups have completed this activity. Reset a group to start the activity again."
              >
                <Button
                  sx={{ width: '100%' }}
                  variant="contained"
                  onClick={startActivityHandler}
                  color="play"
                  dataTestId="start-activity-button"
                  disabled={
                    (!isTeacher && onlineActivityStatus === activityStatus.notStarted) ||
                    isAllActivityGroupsCompleted
                  }
                >
                  Start Activity
                </Button>
              </TooltipDisabledButton>
            )}
            {buttonConfig.showEnd && (
              <Button
                variant="outlined"
                onClick={endActivityHandler}
                color="error"
                dataTestId="end-session-button"
              >
                End session
              </Button>
            )}
            {buttonConfig.showReview && (
              <Button
                variant="outlined"
                dataTestId="review-answers-button"
                onClick={() => reviewQuestionsHandler()}
              >
                Review Answers
              </Button>
            )}
          </>
        ),
        testId: 'online'
      };
    }
    // Offline Activity view
    if (numberOfQuestions === 0 && !!groupActivityId && !!sectionStudentList.length) {
      return {
        showSkeleton: false,
        onlineMode: false,
        actionButtons: undefined,
        img: offlineActivityUrlImg,
        title: 'No Online Practice',
        description:
          'This lesson is designed to be completed offline, so there’s no online practice available. Dive into worksheets to get started!',
        testId: 'offline'
      };
    }
    // Activities not started yet by Teacher
    if (!isTeacher && !groupActivityId) {
      return {
        showSkeleton: false,
        onlineMode: true,
        actionButtons: (
          <>
            <Button
              variant="contained"
              color="play"
              data-testid="start-activity-button"
              disabled
            >
              Start Activity
            </Button>
          </>
        ),
        img: notStartedImgUrlImg,
        title: 'Online Group Activity Not Ready Yet',
        description:
          'Looks like this practice isn’t ready yet. Your teacher will let you know when it’s time to begin. Check back soon!',
        testId: 'non-started-activity'
      };
    }
    // No students in the section
    if (isTeacher && !sectionStudentList.length) {
      return {
        showSkeleton: false,
        actionButtons: undefined,
        img: notStartedImgUrlImg,
        title: 'No Students In This Section',
        description: 'Please add students to this section to get started!',
        testId: 'no-students'
      };
    }
    // When Students finished their practice and the activity not ended yet
    if (
      !isTeacher &&
      currentGroupActivity.group_status === groupOLPStatus.ended &&
      onlineActivityStatus === activityStatus.started
    ) {
      return {
        showSkeleton: false,
        actionButtons: undefined,
        img: completedPracticeUrlImg,
        title: 'Group Activity Complete!',
        description:
          'You’ll be able to review your answers once your teachers ends this practice session!',
        testId: 'completed-practice'
      };
    }
    // The student is absent
    if (
      !!groupActivityId &&
      !isTeacher &&
      isStudentAbsent &&
      !!sectionStudentList.length
    ) {
      return {
        showSkeleton: false,
        actionButtons: undefined,
        img: absentStudentUrlImg,
        title: 'Your Teacher Marked You Absent',
        description:
          'Let your teacher know if you are present in class and ready to take this practice!',
        testId: 'absent-student'
      };
    }
    // Skeleton when loading
    return { showSkeleton: true };
  };

  const viewConfig = getViewConfig();

  return (
    <ShadowScrollbar
      autoHide={false}
      style={{
        height: 'calc(100vh - 162px)',
        padding: '1.5em',
      }}
    >
      <PacingGuideInfoCard
        guideInfoText={pacingGuideLessonData?.group_activity_instruction ?? ''}
        taskDeliveryText={pacingGuideLessonData?.group_activity_delivery ?? ''}
      />
      {viewConfig.showSkeleton && <ActivityFactorySkeleton />}
      {!viewConfig.showSkeleton && (
        <>
          <ActionCard
            dataTestId={`action-card-${viewConfig.testId}`}
            modes={<>
              <ButtonGroup aria-label="mode selector">
                {!!viewConfig.onlineMode &&
                  <Button color="ogaModes" variant='contained' sx={{ backgroundColor: '#fd9e2e' }}>Online Group Activity</Button>}
                {!!viewConfig.onlineMode && isTeacher &&
                  <Button color="ogaModes"
                    variant="contained"
                    dataTestId="bellringer-button"
                    onClick={() => setIsBellringerDialogOpen(true)}
                  >
                    Bell-Ringer Mode
                  </Button>}
                <WorksheetsButton />
              </ButtonGroup>
            </>}
            actionButtons={viewConfig.actionButtons}
          />
          <ShowIf If={!!viewConfig.img}>
            <InformationCard
              dataTestId={`info-card-${viewConfig.testId}`}
              img={viewConfig.img as string}
              title={viewConfig.title as string}
              description={viewConfig.description}
            />
          </ShowIf>
          <GroupActivity />
          <BellringerContextDialog
            open={isBellringerDialogOpen}
            sections={teacherSections}
            onCancel={() => setIsBellringerDialogOpen(false)}
            onContinue={launchBellringer}
          />
        </>
      )}
    </ShadowScrollbar>
  );
};

const withWrappers = (Component: React.FC) => {
  return (props: {}) => (
    <StartActivityProvider>
      <EndSessionProvider>
        <Component {...props} />
      </EndSessionProvider>
    </StartActivityProvider>
  );
}
export default React.memo(withWrappers(ActivityFactory));